import {
  defaultGeneration,
  generationYearsLabel as yearsLabel,
  makeById,
  modelById,
  SEGMENT_LABELS,
  type CarModel,
  type Generation,
} from '@kaufcheck/catalog';
import { hasKnowledge, loadKnowledge } from '@kaufcheck/catalog/knowledge';
import { useQuery } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cn } from '../../lib/format';

const SEVERITY_LABELS = { hoch: 'Wichtig', mittel: 'Beachten', gering: 'Am Rande' } as const;

function IssueBody({ issue }: { issue: Generation['issues'][number] }) {
  return (
    <>
      <p className="issue__affects">Betrifft: {issue.affects}</p>
      <p>{issue.detail}</p>
      <p className="issue__check">
        <strong>Darauf achten:</strong> {issue.check}
      </p>
      <p className="issue__sources">
        Quelle{issue.sources.length > 1 ? 'n' : ''}:{' '}
        {issue.sources.map((source, index) => (
          <span key={source.url}>
            {index > 0 && ', '}
            <a href={source.url} target="_blank" rel="noopener nofollow">
              {source.label}
            </a>
          </span>
        ))}
      </p>
    </>
  );
}

function IssueHead({ issue }: { issue: Generation['issues'][number] }) {
  return (
    <>
      <span className={cn('issue__severity', `issue__severity--${issue.severity}`)}>
        {SEVERITY_LABELS[issue.severity]}
      </span>
      <strong>{issue.title}</strong>
    </>
  );
}

function GenerationDetails({
  generation,
  compact,
  owner,
}: {
  generation: Generation;
  compact: boolean;
  owner: boolean;
}) {
  const IssuesHeading = owner ? null : 'h4';
  return (
    <div className="model-generation">
      {!compact && generation.strengths.length > 0 && (
        <section aria-label="Stärken">
          <h4>Stärken</h4>
          <ul className="check-list">
            {generation.strengths.map((strength) => (
              <li key={strength}>{strength}</li>
            ))}
          </ul>
        </section>
      )}
      {generation.issues.length > 0 && (
        <section aria-label={owner ? undefined : 'Bekannte Schwachstellen'}>
          {IssuesHeading && <IssuesHeading>Bekannte Schwachstellen</IssuesHeading>}
          <ul className="issue-list">
            {generation.issues.map((issue) =>
              compact ? (
                <li key={issue.title} className="issue issue--compact">
                  <details>
                    <summary className="issue__head">
                      <IssueHead issue={issue} />
                    </summary>
                    <IssueBody issue={issue} />
                  </details>
                </li>
              ) : (
                <li key={issue.title} className="issue">
                  <p className="issue__head">
                    <IssueHead issue={issue} />
                  </p>
                  <IssueBody issue={issue} />
                </li>
              ),
            )}
          </ul>
        </section>
      )}
      {/* Engine advice and inspection tips are for buyers, not for owners. */}
      {!owner &&
        (generation.engines.recommended.length > 0 || generation.engines.caution.length > 0) && (
          <section aria-label="Motoren" className="engine-notes">
            {generation.engines.recommended.length > 0 && (
              <div>
                <h4>Empfehlenswert</h4>
                <ul>
                  {generation.engines.recommended.map((engine) => (
                    <li key={engine.name}>
                      <strong>{engine.name}</strong> – {engine.why}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {generation.engines.caution.length > 0 && (
              <div>
                <h4>Mit Vorsicht</h4>
                <ul>
                  {generation.engines.caution.map((engine) => (
                    <li key={engine.name}>
                      <strong>{engine.name}</strong> – {engine.why}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
        )}
      {!owner && generation.tips.length > 0 && (
        <section aria-label="Tipps für die Besichtigung">
          <h4>Tipps für die Besichtigung</h4>
          <ul className="check-list">
            {generation.tips.map((tip) => (
              <li key={tip}>{tip}</li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

interface ModelKnowledgeViewProps {
  model: CarModel;
  yearMin?: number | null;
  yearMax?: number | null;
  /** Heading level of the generation title (below the page or section heading). */
  generationHeading?: 'h2' | 'h3' | 'h4';
  /** Weaknesses as expandable rows, without strengths – inside a listing check. */
  compact?: boolean;
  /** Generation shown first, e.g. the one a user chose for their car. */
  initialGenerationId?: string | null;
  /** For the owner of such a car („Mein Auto“): weaknesses only, no buying advice. */
  owner?: boolean;
  /** Shown above the generations for the shown one, e.g. its photo (the model page). */
  photo?: (generation: Generation) => ReactNode;
}

/** Generations with strengths, weaknesses (with sources), engines and inspection tips. */
export function ModelKnowledgeView({
  model,
  yearMin = null,
  yearMax = null,
  generationHeading: GenerationHeading = 'h3',
  compact = false,
  initialGenerationId = null,
  owner = false,
  photo,
}: ModelKnowledgeViewProps) {
  const [selected, setSelected] = useState<string | null>(initialGenerationId);
  const generation =
    model.generations.find((item) => item.id === selected) ??
    defaultGeneration(model, yearMin, yearMax);
  const twin = model.twinOf ? modelById(model.twinOf) : null;
  const twinName = twin ? `${makeById(twin.makeId)?.name ?? ''} ${twin.model}`.trim() : '';

  return (
    <div className="model-knowledge">
      {twin && (
        <p className="model-insights__note">
          Technisch weitgehend baugleich mit{' '}
          {hasKnowledge(twin.id) ? <Link to={`/modelle/${twin.id}`}>{twinName}</Link> : twinName} –
          die dort bekannten Schwachstellen gelten meist auch hier.
        </p>
      )}
      {photo && generation && photo(generation)}
      {model.generations.length > 1 && (
        <div className="segmented" role="group" aria-label="Generation wählen">
          {model.generations.map((item) => (
            <button
              key={item.id}
              type="button"
              className={cn('segmented__option', item.id === generation?.id && 'is-active')}
              aria-pressed={item.id === generation?.id}
              onClick={() => setSelected(item.id)}
            >
              {item.name} <span className="segmented__meta">{yearsLabel(item.years)}</span>
            </button>
          ))}
        </div>
      )}
      {generation && (
        <>
          <GenerationHeading className="model-insights__generation">
            {generation.name}
            {generation.code ? ` (${generation.code})` : ''}, {yearsLabel(generation.years)}
          </GenerationHeading>
          <GenerationDetails generation={generation} compact={compact} owner={owner} />
        </>
      )}
      {owner ? (
        <p className="model-insights__disclaimer">
          Bekannte Schwachstellen laut ADAC, TÜV-Report und Fachpresse – sie treten nicht bei jedem
          Auto auf. Merkst du Anzeichen davon, lass dein Auto in einer Werkstatt prüfen.
        </p>
      ) : (
        <p className="model-insights__disclaimer">
          Bekannte Schwachstellen laut ADAC, TÜV-Report und Fachpresse – sie treten nicht bei jedem
          Auto auf. Wie es um das Auto vor dir steht, zeigt erst die Besichtigung, im Zweifel mit
          einer Werkstatt.{' '}
          <Link to="/auto-besichtigung-checkliste">Zur Besichtigungs-Checkliste</Link>
        </p>
      )}
    </div>
  );
}

interface ModelInsightsProps {
  modelId: string;
  yearMin: number | null;
  yearMax: number | null;
}

/** Researched knowledge about the searched model (loaded on demand), on the search page. */
export function ModelInsights({ modelId, yearMin, yearMax }: ModelInsightsProps) {
  const entry = modelById(modelId);
  const knowledge = useQuery({
    queryKey: ['model-knowledge', modelId],
    queryFn: () => loadKnowledge(modelId),
    enabled: hasKnowledge(modelId),
    staleTime: Infinity,
  });
  if (!entry || !hasKnowledge(modelId) || !knowledge.data) return null;
  const model = knowledge.data;
  const headingId = `insights-${model.id}`;

  return (
    <section className="section section--muted model-insights" aria-labelledby={headingId}>
      <div className="container">
        <p className="eyebrow">{SEGMENT_LABELS[model.segment]}</p>
        <h2 id={headingId} className="section__title">
          {model.make} {model.model}: Das solltest du wissen
        </h2>
        <p className="section__lead">{model.summary}</p>
        <ModelKnowledgeView model={model} yearMin={yearMin} yearMax={yearMax} />
        <p>
          <Link to={`/modelle/${model.id}`}>
            Alle Infos zu {model.make} {model.model}
          </Link>
        </p>
      </div>
    </section>
  );
}
